import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrashAlt,
  faInfoCircle,
  faConciergeBell,
} from "@fortawesome/free-solid-svg-icons";

import { StandardURL } from "../App";

// Set app element for react-modal
Modal.setAppElement("#root");

const AmenitiesPage = () => {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [amenityNameInput, setAmenityNameInput] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const propertyId = localStorage.getItem("selectedPropertyId");

  useEffect(() => {
    fetchAmenities();
  }, [token, propertyId]);

  const fetchAmenities = async () => {
    setLoading(true);
    setError(null);
    if (!token || !propertyId) {
      setError(
        "Authentication token or property ID missing. Please ensure you are logged in and a property is selected."
      );
      toast.error("Authentication token or property ID missing.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/amenities/fetch/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAmenities(Array.isArray(response.data.response) ? response.data.response : (response.data.response ? [response.data.response] : []));
    } catch (err) {
      console.error("Error fetching amenities:", err);
      if (axios.isAxiosError(err) && err.response && err.response.status === 404) {
        setAmenities([]);
        toast.info("No amenities found for this property. Add some!");
      } else {
        setError(err.message || "Failed to fetch amenities.");
        toast.error(
          err.response?.data?.response || "Failed to fetch amenities. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddAmenityClick = () => {
    setAmenityNameInput("");
    setFormErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setAmenityNameInput("");
    setFormErrors({});
  };

  const validateForm = () => {
    let newErrors = {};
    if (!amenityNameInput.trim()) {
      newErrors.amenityName = "Amenity name is required.";
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addAmenity = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please correct the errors in the form.");
      return;
    }

    setLoading(true);
    try {
      const newAmenityData = {
        amenity_name: amenityNameInput,
      };

      const response = await axios.post(
        `${StandardURL}/iNyumba/amenities/post/${propertyId}`,
        newAmenityData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.response || "Amenity added successfully!");
      closeModal();
      fetchAmenities();
    } catch (err) {
      console.error("Error adding amenity:", err);
      toast.error(err.response?.data?.response || "Failed to add amenity. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAmenityNavigation = (amenity) => {
    localStorage.setItem("selectedAmenityId", amenity.amenity_id);
    navigate("/landlord/updateamenity");
  };

  const handleDeleteAmenity = async (amenityIdToDelete) => {
    if (window.confirm("Are you sure you want to delete this amenity? This action cannot be undone.")) {
      setLoading(true);
      try {
        const response = await axios.delete(
          `${StandardURL}/iNyumba/amenities/delete/${amenityIdToDelete}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(response.data.response || "Amenity deleted successfully!");
        fetchAmenities();
      } catch (err) {
        console.error("Error deleting amenity:", err);
        toast.error(err.response?.data?.response || "Failed to delete amenity. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading && amenities.length === 0 && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <Oval
          height={50}
          width={50}
          color="#4F46E5"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#A5B4FC"
          strokeWidth={4}
          strokeWidthSecondary={4}
        />
        <p className="ml-3 text-lg text-gray-700">Loading amenities...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-semibold text-red-600 mb-4">Error!</h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={fetchAmenities}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
     
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4 sm:mb-0">
            <FontAwesomeIcon icon={faConciergeBell} className="mr-3 text-indigo-600" />
            Property Amenities
          </h1>
          <button
            onClick={handleAddAmenityClick}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-all duration-300 flex items-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add New Amenity
          </button>
        </div>

        {amenities.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    Amenity Name
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {amenities.map((amenity) => (
                  <tr key={amenity.amenity_id || amenity.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                      {amenity.amenity_name}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap flex items-center">
                      <button
                        onClick={() => handleUpdateAmenityNavigation(amenity)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-1.5 px-3 rounded-md text-sm transition-colors duration-200 mr-2 flex items-center"
                        title="Update"
                      >
                        <FontAwesomeIcon icon={faEdit} className="mr-1" /> Update
                      </button>
                      <button
                        onClick={() => handleDeleteAmenity(amenity.amenity_id)}
                        className="bg-red-500 hover:bg-red-600 text-white font-bold py-1.5 px-3 rounded-md text-sm transition-colors duration-200 flex items-center"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} className="mr-1" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">No amenities found for this property.</p>
            <p className="text-md text-gray-500 mt-2">Click "Add New Amenity" to get started.</p>
          </div>
        )}
      </div>

      {/* Add Amenity Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Add New Amenity"
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-800 bg-opacity-75"
        overlayClassName="fixed inset-0 bg-gray-800 bg-opacity-75"
      >
        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md transform transition-all duration-300 scale-100 opacity-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
            Add New Amenity
          </h2>
          <form onSubmit={addAmenity}>
            <div className="mb-4">
              <label htmlFor="amenityName" className="block text-sm font-medium text-gray-700">
                Amenity Name
              </label>
              <input
                id="amenityName"
                type="text"
                value={amenityNameInput}
                onChange={(e) => {
                  setAmenityNameInput(e.target.value);
                  if (formErrors.amenityName) setFormErrors({ ...formErrors, amenityName: null });
                }}
                placeholder="e.g., Swimming Pool, Gym"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
              {formErrors.amenityName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.amenityName}</p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-300"
                disabled={loading}
              >
                {loading ? (
                  <Oval height={20} width={20} color="#FFF" secondaryColor="#A5B4FC" strokeWidth={5} strokeWidthSecondary={5} />
                ) : (
                  "Add Amenity"
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default AmenitiesPage;